import React from "react";
import { Card, CardContent, CardHeader, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { 
  Timeline,
  TrendingUp,
  Eco,
  CloudQueue,
  Waves,
  Brightness5,
  LocationOn,
  CropFree,
  AttachMoney,
  History
} from "@material-ui/icons";
import { Bar } from "react-chartjs-2";

const useStyles = makeStyles(theme => ({
  card: {
    height: "100%"
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  metricCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    height: "100%"
  },
  metricIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  chartContainer: {
    marginTop: theme.spacing(4),
    height: "300px"
  },
  metricValue: {
    fontWeight: "bold",
    fontSize: "1.5rem",
    marginBottom: theme.spacing(1)
  },
  metricTitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  metricTrend: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
  },
  applicantInfo: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius
  }
}));

export default function MetricsPanel({ data }) {
  const classes = useStyles();

  // Extract proper metrics from API response data
  const getFarmSizeFromData = () => {
    // Check for various field names that might contain farm size
    if (data?.farmSize) return `${data.farmSize} ha`;
    if (data?.area_hectares) return `${data.area_hectares} ha`;
    if (data?.yieldForecast?.metadata?.land_size) return `${data.yieldForecast.metadata.land_size} ha`;
    if (data?.riskAssessment?.metadata?.land_size) return `${data.riskAssessment.metadata.land_size} ha`;
    if (data?.ndvi?.metadata?.farm_area_hectares) return `${data.ndvi.metadata.farm_area_hectares} ha`;
    return "N/A";
  };
  
  const getCropTypeFromData = () => {
    // Check for various field names that might contain crop type
    if (data?.cropType) return data.cropType;
    if (data?.crop_type) return data.crop_type;
    if (data?.yieldForecast?.metadata?.crop_type) return data.yieldForecast.metadata.crop_type;
    if (data?.riskAssessment?.metadata?.crop_type) return data.riskAssessment.metadata.crop_type;
    if (data?.ndvi?.metadata?.crop_type) return data.ndvi.metadata.crop_type;
    return "Unknown";
  };
  
  const getNdviValueFromData = () => {
    if (!data?.ndvi) return "N/A";
    
    // Check for different API response formats
    if (data.ndvi.current_value !== undefined) {
      return data.ndvi.current_value.toFixed(2);
    }
    
    if (data.ndvi.value !== undefined) {
      return data.ndvi.value.toFixed(2);
    }
    
    if (typeof data.ndvi === 'number') {
      return data.ndvi.toFixed(2);
    }
    
    return "N/A";
  };
  
  const getYieldValueFromData = () => {
    if (!data?.yieldForecast) return "N/A";
    
    // Check for different API response formats
    if (data.yieldForecast.yield_value !== undefined) {
      return `${data.yieldForecast.yield_value.toFixed(2)} ${data.yieldForecast.yield_unit || 'tons/ha'}`;
    }
    
    if (data.yieldForecast.predicted !== undefined) {
      return `${data.yieldForecast.predicted.toFixed(2)} tons/ha`;
    }
    
    if (typeof data.yieldForecast === 'number') {
      return `${data.yieldForecast.toFixed(2)} tons/ha`;
    }
    
    return "N/A";
  };
  
  const getLastUpdatedFromData = () => {
    // Try to get the most recent update date from any data source
    const dates = [];
    
    if (data?.ndvi?.last_updated) dates.push(new Date(data.ndvi.last_updated));
    if (data?.yieldForecast?.last_updated) dates.push(new Date(data.yieldForecast.last_updated));
    if (data?.riskAssessment?.last_updated) dates.push(new Date(data.riskAssessment.last_updated));
    
    if (dates.length === 0) return "Unknown";
    
    // Sort dates in descending order and take the most recent
    dates.sort((a, b) => b - a);
    return dates[0].toLocaleDateString();
  };
  
  const metrics = [
    {
      title: "Farm Name",
      value: data?.name || data?.riskAssessment?.metadata?.farm_name || "Unnamed Farm",
      icon: <LocationOn />,
      trend: "Farm Identity"
    },
    {
      title: "Land Size",
      value: getFarmSizeFromData(),
      icon: <CropFree />,
      trend: "Total Area"
    },
    {
      title: "Crop Type",
      value: getCropTypeFromData(),
      icon: <Eco />,
      trend: "Current Crop"
    },
    {
      title: "NDVI Value",
      value: getNdviValueFromData(),
      icon: <Timeline />,
      trend: "Vegetation Health"
    },
    {
      title: "Yield Forecast",
      value: getYieldValueFromData(),
      icon: <TrendingUp />,
      trend: "Expected Output"
    },
    {
      title: "Last Updated",
      value: getLastUpdatedFromData(),
      icon: <History />,
      trend: "Data Freshness"
    }
  ];

  // Extract historical data for charts from API response
  const getHistoricalNdviData = () => {
    // If we have historical NDVI values, use them
    if (data?.ndvi?.historical_values?.length > 0) {
      return {
        labels: data.ndvi.historical_values.map(item => {
          const date = new Date(item.date);
          return `${date.getMonth()+1}/${date.getDate()}`;
        }),
        values: data.ndvi.historical_values.map(item => Math.round(item.value * 100))
      };
    }
    
    // If we have yield forecast with historical values
    if (data?.yieldForecast?.historical_yields?.length > 0) {
      return {
        labels: data.yieldForecast.historical_yields.map(item => {
          const date = new Date(item.date);
          return `${date.getMonth()+1}/${date.getDate()}`;
        }),
        values: data.yieldForecast.historical_yields.map(item => Math.round(item.ndvi_value * 100) || Math.round(item.value * 10))
      };
    }
    
    // Default fallback - last 6 months with synthetic data
    const labels = [];
    const currentDate = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      labels.push(`${date.getMonth()+1}/${date.getDate()}`);
    }
    
    // Create decreasing then increasing NDVI values (representing a seasonal cycle)
    const baseValue = data?.ndvi?.value ? Math.round(data.ndvi.value * 100) : 65;
    const values = [
      baseValue - 10,
      baseValue - 5,
      baseValue,
      baseValue + 5,
      baseValue + 2,
      baseValue - 3
    ];
    
    return { labels, values };
  };
  
  // Get soil moisture or precipitation data if available
  const getSoilMoistureData = () => {
    // If we have weather data with precipitation, use that
    if (data?.weather?.precipitation_data?.length > 0) {
      return data.weather.precipitation_data.map(item => item.value);
    }
    
    if (data?.soilData?.moisture_readings?.length > 0) {
      return data.soilData.moisture_readings.map(item => item.value);
    }
    
    // Create synthetic data that's somewhat inverse to NDVI (representing precipitation)
    const ndviData = getHistoricalNdviData().values;
    return ndviData.map(value => Math.round(100 - (value * 0.7)));
  };
  
  const historicalData = getHistoricalNdviData();
  
  const chartData = {
    labels: historicalData.labels,
    datasets: [
      {
        label: "Vegetation Index",
        data: historicalData.values,
        backgroundColor: "#4CAF50",
      },
      {
        label: "Soil Moisture",
        data: getSoilMoistureData(),
        backgroundColor: "#2196F3",
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Index Value (%)"
        }
      }
    },
    plugins: {
      legend: {
        position: "top"
      }
    }
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Environmental Metrics"
        className={classes.cardHeader}
      />
      <CardContent>
        <div className={classes.applicantInfo}>
          <Typography variant="h6" gutterBottom>
            Applicant Details
          </Typography>
          <Typography variant="body1">
            Name: {data?.name || "N/A"}
          </Typography>
          <Typography variant="body1">
            Crop Type: {data?.crop || "N/A"}
          </Typography>
          <Typography variant="body1">
            Purpose: {data?.loanPurpose || "N/A"}
          </Typography>
        </div>
        <Grid container spacing={3}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <div className={classes.metricCard}>
                {React.cloneElement(metric.icon, { className: classes.metricIcon })}
                <Typography variant="subtitle2" className={classes.metricTitle}>
                  {metric.title}
                </Typography>
                <Typography variant="h5" className={classes.metricValue}>
                  {metric.value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {metric.trend}
                </Typography>
              </div>
            </Grid>
          ))}
        </Grid>
        <div className={classes.chartContainer}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </CardContent>
    </Card>
  );
}
